import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HiOutlinePlus, HiOutlineSearch, HiOutlinePencil, HiOutlineTrash, HiOutlineBriefcase, HiOutlineUser, HiOutlineDocumentText, HiOutlineCalendar, HiOutlineExclamationCircle, HiOutlineEye, HiOutlineGlobeAlt } from 'react-icons/hi';
import LoadingSpinner from '../components/LoadingSpinner';
import { Application, Company, Contact, Resume, CreateApplicationData, UpdateApplicationData } from '../types';
import { applicationsService } from '../services/applicationsService';
import { companiesService } from '../services/companiesService';
import { contactsService } from '../services/contactsService';
import { resumesService } from '../services/resumesService';
import toast from 'react-hot-toast';
import { useEscapeModal, hasFormChanges } from '../utils/useEscapeModal';

interface ApplicationFormData {
  company: string;
  role: string;
  resume?: string;
  referralContact?: string;
  status: 'applied' | 'under_review' | 'phone_screening' | 'technical_test' | 'interview_scheduled' | 'interviewed' | 'final_round' | 'offer_received' | 'offer_accepted' | 'offer_declined' | 'rejected' | 'withdrawn';
  appliedDate: string;
  source: 'linkedin' | 'indeed' | 'glassdoor' | 'company_website' | 'referral' | 'recruiter' | 'cold_email' | 'other';
  applicationUrl?: string;
  notes?: string;
  coverLetter?: string;
  followUpDate?: string;
  followUpNotes?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  salary?: {
    offered?: number;
    negotiated?: number;
    currency: string;
    period: 'hourly' | 'monthly' | 'yearly';
  };
}

const Applications: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [applications, setApplications] = useState<Application[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [, setAllContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [contactsLoading, setContactsLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingApplication, setEditingApplication] = useState<Application | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('priority-desc');

  // Format date to "8th May 2025" format
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    
    const getOrdinalSuffix = (day: number): string => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
  };

  // Get contact ID from referral contact
  const getContactId = (contact: string | Contact | undefined): string | null => {
    if (!contact) return null;
    return typeof contact === 'string' ? contact : contact._id;
  };

  const [formData, setFormData] = useState<ApplicationFormData>({
    company: '',
    role: '',
    resume: '',
    referralContact: '',
    status: 'applied',
    appliedDate: new Date().toISOString().split('T')[0],
    source: 'other',
    applicationUrl: '',
    notes: '',
    coverLetter: '',
    followUpDate: '',
    followUpNotes: '',
    priority: 'medium',
    salary: {
      offered: undefined,
      negotiated: undefined,
      currency: 'INR',
      period: 'yearly'
    }
  });

  // Initial form data for change detection
  const [initialFormData, setInitialFormData] = useState<ApplicationFormData>({
    company: '',
    role: '',
    resume: '',
    referralContact: '',
    status: 'applied',
    appliedDate: new Date().toISOString().split('T')[0],
    source: 'other',
    applicationUrl: '',
    notes: '',
    coverLetter: '',
    followUpDate: '',
    followUpNotes: '',
    priority: 'medium',
    salary: {
      offered: undefined,
      negotiated: undefined,
      currency: 'INR',
      period: 'yearly'
    }
  });

  const statusOptions = [
    { value: 'applied', label: 'Applied', color: 'bg-blue-100 text-blue-800' },
    { value: 'under_review', label: 'Under Review', color: 'bg-purple-100 text-purple-800' },
    { value: 'phone_screening', label: 'Phone Screening', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'technical_test', label: 'Technical Test', color: 'bg-orange-100 text-orange-800' },
    { value: 'interview_scheduled', label: 'Interview Scheduled', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'interviewed', label: 'Interviewed', color: 'bg-cyan-100 text-cyan-800' },
    { value: 'final_round', label: 'Final Round', color: 'bg-teal-100 text-teal-800' },
    { value: 'offer_received', label: 'Offer Received', color: 'bg-green-100 text-green-800' },
    { value: 'offer_accepted', label: 'Offer Accepted', color: 'bg-green-200 text-green-900' },
    { value: 'offer_declined', label: 'Offer Declined', color: 'bg-gray-100 text-gray-800' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
    { value: 'withdrawn', label: 'Withdrawn', color: 'bg-gray-100 text-gray-800' }
  ];

  const sourceOptions = [
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'indeed', label: 'Indeed' },
    { value: 'glassdoor', label: 'Glassdoor' },
    { value: 'company_website', label: 'Company Website' },
    { value: 'referral', label: 'Referral' },
    { value: 'recruiter', label: 'Recruiter' },
    { value: 'cold_email', label: 'Cold Email' },
    { value: 'other', label: 'Other' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-800' },
    { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-800' },
    { value: 'high', label: 'High', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' }
  ];

  const sortOptions = [
    { value: 'priority-desc', label: 'Priority (High to Low)' },
    { value: 'priority-asc', label: 'Priority (Low to High)' },
    { value: 'appliedDate-desc', label: 'Applied Date (Newest First)' },
    { value: 'appliedDate-asc', label: 'Applied Date (Oldest First)' },
    { value: 'company-asc', label: 'Company (A-Z)' },
    { value: 'company-desc', label: 'Company (Z-A)' },
    { value: 'status-asc', label: 'Status (A-Z)' },
    { value: 'followUpDate-asc', label: 'Follow Up (Earliest First)' },
    { value: 'followUpDate-desc', label: 'Follow Up (Latest First)' }
  ];

  useEffect(() => {
    fetchData();
  }, []);



  useEffect(() => {
    if (formData.company) {
      fetchContactsByCompany(formData.company);
    } else {
      setFilteredContacts([]);
    }
  }, [formData.company]);

  const fetchData = async (): Promise<void> => {
    try {
      setLoading(true);
      const [applicationsRes, companiesRes, contactsRes, resumesRes] = await Promise.all([
        applicationsService.getApplications({ limit: 100 }),
        companiesService.getCompanies({ limit: 100 }), 
        contactsService.getContacts({ limit: 100 }), 
        resumesService.getResumes({ limit: 100 }) 
      ]);

      setApplications(applicationsRes.data);
      setCompanies(companiesRes.data);
      setAllContacts(contactsRes.data);
      setResumes(resumesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchContactsByCompany = async (companyId: string): Promise<void> => {
    try {
      setContactsLoading(true);
      const contactsRes = await contactsService.getContactsByCompany(companyId, { limit: 100 });
      setFilteredContacts(contactsRes.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setFilteredContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      const applicationData: CreateApplicationData | UpdateApplicationData = {
        company: formData.company,
        role: formData.role,
        resume: formData.resume || undefined,
        referralContact: formData.referralContact || undefined,
        status: formData.status,
        appliedDate: formData.appliedDate,
        source: formData.source,
        applicationUrl: formData.applicationUrl || undefined,
        notes: formData.notes,
        coverLetter: formData.coverLetter,
        followUpDate: formData.followUpDate || undefined,
        followUpNotes: formData.followUpNotes,
        priority: formData.priority,
        salary: formData.salary?.offered || formData.salary?.negotiated ? formData.salary : undefined
      };

      if (editingApplication) {
        await applicationsService.updateApplication(editingApplication._id, applicationData);
        toast.success('Application updated successfully');
      } else {
        await applicationsService.createApplication(applicationData as CreateApplicationData);
        toast.success('Application created successfully');
      }
      setShowModal(false);
      await fetchData();
    } catch (error) {
      console.error('Error saving application:', error);
      toast.error('Failed to save application');
    }
  };

  const handleEdit = (application: Application): void => {
    setEditingApplication(application);
    const editFormData = {
      company: typeof application.company === 'string' ? application.company : application.company._id,
      role: application.role || '',
      resume: typeof application.resume === 'string' ? application.resume : application.resume?._id || '',
      referralContact: typeof application.referralContact === 'string' ? application.referralContact : application.referralContact?._id || '',
      status: application.status,
      appliedDate: application.appliedDate.split('T')[0],
      source: application.source || 'other' as const,
      applicationUrl: application.applicationUrl || '',
      notes: application.notes || '',
      coverLetter: application.coverLetter || '',
      followUpDate: application.followUpDate ? application.followUpDate.split('T')[0] : '',
      followUpNotes: application.followUpNotes || '',
      priority: application.priority || 'medium' as const,
      salary: application.salary || {
        offered: undefined,
        negotiated: undefined,
        currency: 'INR',
        period: 'yearly' as const
      }
    };
    setFormData(editFormData);
    setInitialFormData(editFormData);
    setShowModal(true);
  };

  // Handle edit mode from ApplicationDetail navigation
  useEffect(() => {
    const state = location.state as { editApplication?: Application };
    if (state?.editApplication && applications.length > 0) {
      handleEdit(state.editApplication);
      // Clear the state to prevent re-triggering
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, applications, location.pathname, navigate]);

  const handleDelete = async (id: string): Promise<void> => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        await applicationsService.deleteApplication(id);
        toast.success('Application deleted successfully');
        await fetchData();
      } catch (error) {
        console.error('Error deleting application:', error);
        toast.error('Failed to delete application');
      }
    }
  };

  const resetForm = (): void => {
    const defaultFormData = {
      company: '',
      role: '',
      resume: '',
      referralContact: '',
      status: 'applied' as const,
      appliedDate: new Date().toISOString().split('T')[0],
      source: 'other' as const,
      applicationUrl: '',
      notes: '',
      coverLetter: '',
      followUpDate: '',
      followUpNotes: '',
      priority: 'medium' as const,
      salary: {
        offered: undefined,
        negotiated: undefined,
        currency: 'INR',
        period: 'yearly' as const
      }
    };

    setFormData(defaultFormData);
    setInitialFormData(defaultFormData);
    setEditingApplication(null);
    setFilteredContacts([]);
    setShowModal(false);
  };

  // Check if form has unsaved changes
  const hasUnsavedChanges = hasFormChanges(formData, initialFormData);

  // Handle ESC key to close modal with confirmation for unsaved changes
  useEscapeModal({
    isOpen: showModal,
    onClose: resetForm,
    hasUnsavedChanges,
    confirmMessage: 'You have unsaved changes. Are you sure you want to close without saving?'
  });

  const getStatusColor = (status: string): string => {
    const statusOption = statusOptions.find(s => s.value === status);
    return statusOption ? statusOption.color : 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority: string): string => {
    const priorityOption = priorityOptions.find(p => p.value === priority);
    return priorityOption ? priorityOption.color : 'bg-gray-100 text-gray-800';
  };

  const getCompanyName = (company: string | Company): string => {
    return typeof company === 'string' ? 'Unknown Company' : company?.name || 'Unknown Company';
  };

  const getResumeName = (resume: string | Resume | undefined): string => {
    if (!resume) return 'No resume';
    return typeof resume === 'string' ? 'Resume' : resume?.title || resume?.originalName || 'Resume';
  };

  const getContactName = (contact: string | Contact | undefined): string => {
    if (!contact) return 'No contact';
    return typeof contact === 'string' ? 'Contact' : contact?.name || 'Contact';
  };

  const getPriorityValue = (priority: string): number => {
    const priorityMap = { low: 1, medium: 2, high: 3, urgent: 4 };
    return priorityMap[priority as keyof typeof priorityMap] || 2;
  };

  const sortApplications = (apps: Application[], sortBy: string): Application[] => {
    const [field, direction] = sortBy.split('-');
    
    return [...apps].sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (field) {
        case 'appliedDate':
          aValue = new Date(a.appliedDate).getTime();
          bValue = new Date(b.appliedDate).getTime();
          break;
        case 'priority':
          aValue = getPriorityValue(a.priority || 'medium');
          bValue = getPriorityValue(b.priority || 'medium');
          break;
        case 'company':
          aValue = getCompanyName(a.company).toLowerCase();
          bValue = getCompanyName(b.company).toLowerCase();
          break;
        case 'status':
          aValue = a.status.toLowerCase();
          bValue = b.status.toLowerCase();
          break;
        case 'followUpDate':
          aValue = a.followUpDate ? new Date(a.followUpDate).getTime() : 0;
          bValue = b.followUpDate ? new Date(b.followUpDate).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (direction === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  const filteredAndSortedApplications = (() => {
    // First filter
    const filtered = applications.filter(application => {
      const companyName = getCompanyName(application.company);
      
      const matchesSearch = companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (application.role && application.role.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           (application.notes && application.notes.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStatus = !statusFilter || application.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    
    // Then sort
    return sortApplications(filtered, sortBy);
  })();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Applications</h1>
          <p className="mt-1 text-sm text-secondary-600">
            Track your job applications with companies, contacts, and resumes.
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn btn-primary btn-md flex items-center gap-2"
        >
          <HiOutlinePlus className="h-4 w-4" />
          Add Application
        </button>
      </div>

      {/* Search, Filter and Sort */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
          <input
            type="text"
            placeholder="Search applications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input w-full pl-10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input w-full sm:w-48"
        >
          <option value="">All Statuses</option>
          {statusOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="input w-full sm:w-60"
        >
          {sortOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {/* Applications Grid */}
      <div className="grid gap-6">
        {filteredAndSortedApplications.length === 0 ? (
          <div className="text-center py-12">
            <HiOutlineBriefcase className="mx-auto h-12 w-12 text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900">No applications</h3>
            <p className="mt-1 text-sm text-secondary-500">Get started by creating your first application.</p>
            <div className="mt-6">
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="btn btn-primary px-5 py-2"
              >
                <HiOutlinePlus className="h-4 w-4 mr-2" />
                Add Application
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredAndSortedApplications.map((application) => (
              <div key={application._id} className="bg-white rounded-xl shadow-sm border border-secondary-200 hover:shadow-lg hover:border-primary-200 transition-all duration-200 overflow-hidden">
                {/* Header with role and company */}
                <div className="bg-gradient-to-r from-primary-50 to-secondary-50 px-6 py-4 border-b border-secondary-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6 flex-1">
                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() => navigate(`/applications/${application._id}`)}
                          className="text-left w-full"
                        >
                          <h3 className="text-lg font-semibold truncate text-primary-600 transition-colors">
                            {application.role}
                          </h3>
                        </button>
                        <p className="text-sm text-secondary-600 mt-1">
                          at {getCompanyName(application.company)}
                        </p>
                      </div>
                      
                      {/* Status and Priority Badges in header */}
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                          {statusOptions.find(s => s.value === application.status)?.label || application.status}
                        </span>
                        {application.priority && application.priority !== 'medium' && (
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(application.priority)}`}>
                            {priorityOptions.find(p => p.value === application.priority)?.label || application.priority}
                          </span>
                        )}
                      </div>
                      
                      {/* Applied Date in header */}
                      <div className="flex items-center gap-2 text-sm text-secondary-600">
                        <HiOutlineCalendar className="h-4 w-4 text-primary-500" />
                        <span>Applied {formatDate(application.appliedDate)}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-6">
                      <button
                        onClick={() => navigate(`/applications/${application._id}`)}
                        className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-white rounded-lg transition-colors"
                        title="View application details"
                      >
                        <HiOutlineEye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(application)}
                        className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-white rounded-lg transition-colors"
                        title="Edit application"
                      >
                        <HiOutlinePencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(application._id)}
                        className="p-2 text-secondary-500 hover:text-red-600 hover:bg-white rounded-lg transition-colors"
                        title="Delete application"
                      >
                        <HiOutlineTrash className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Application Details */}
                    <div className="space-y-3">
                      {application.resume && (
                        <div className="flex items-center gap-2 text-sm text-secondary-600">
                          <HiOutlineDocumentText className="h-4 w-4 text-primary-500 flex-shrink-0" />
                          <span className="truncate">{getResumeName(application.resume)}</span>
                        </div>
                      )}
                      
                      {application.referralContact && (
                        <div className="flex items-center gap-2 text-sm text-secondary-600">
                          <HiOutlineUser className="h-4 w-4 text-primary-500 flex-shrink-0" />
                          <span className="truncate">Referral: 
                            <button
                              onClick={() => {
                                const contactId = getContactId(application.referralContact);
                                if (contactId) {
                                  navigate(`/contacts/${contactId}`);
                                }
                              }}
                              className="text-primary-600 hover:text-primary-800 hover:underline ml-1 font-medium"
                            >
                              {getContactName(application.referralContact)}
                            </button>
                          </span>
                        </div>
                      )}
                      
                      {application.source && application.source !== 'other' && (
                        <div className="flex items-center gap-2 text-sm text-secondary-600">
                          <HiOutlineBriefcase className="h-4 w-4 text-primary-500 flex-shrink-0" />
                          <span className="truncate">Source: {sourceOptions.find(s => s.value === application.source)?.label}</span>
                        </div>
                      )}
                      
                      {application.applicationUrl && (
                        <div className="flex items-center gap-2 text-sm text-secondary-600">
                          <HiOutlineGlobeAlt className="h-4 w-4 text-primary-500 flex-shrink-0" />
                          <a 
                            href={application.applicationUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-800 hover:underline truncate"
                          >
                            View Job Posting
                          </a>
                        </div>
                      )}
                      
                      {application.salary && (application.salary.offered || application.salary.negotiated) && (
                        <div className="flex items-center gap-2 text-sm text-secondary-600">
                          <span className="text-primary-500 font-medium">💰</span>
                          <span className="truncate">
                            {application.salary.offered && `Offered: ${application.salary.offered.toLocaleString()} ${application.salary.currency}`}
                            {application.salary.negotiated && application.salary.offered && ' | '}
                            {application.salary.negotiated && `Negotiated: ${application.salary.negotiated.toLocaleString()} ${application.salary.currency}`}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Center Column - Follow-up and additional info */}
                    <div className="space-y-3">
                      {/* Follow-up Date */}
                      {application.followUpDate && (
                        <div className={`p-3 rounded-lg ${new Date(application.followUpDate) > new Date() ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50 border border-gray-200'}`}>
                          <div className="flex items-center gap-2 text-sm">
                            <HiOutlineExclamationCircle className={`h-4 w-4 ${new Date(application.followUpDate) > new Date() ? 'text-amber-600' : 'text-gray-500'}`} />
                            <div>
                                                           <span className={`font-medium ${new Date(application.followUpDate) > new Date() ? 'text-amber-700' : 'text-gray-600'}`}>
                               {new Date(application.followUpDate) > new Date() ? 'Follow up: ' : 'Followed up: '}
                               {formatDate(application.followUpDate)}
                             </span>
                              {application.followUpNotes && (
                                <p className={`text-xs mt-1 ${new Date(application.followUpDate) > new Date() ? 'text-amber-600' : 'text-gray-500'}`}>
                                  {application.followUpNotes}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Additional status info if needed */}
                      {(application.status === 'offer_received' || application.status === 'offer_accepted') && (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center gap-2 text-sm text-green-700">
                            <span className="font-medium">🎉</span>
                            <span className="font-medium">
                              {application.status === 'offer_received' ? 'Offer Received!' : 'Offer Accepted!'}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {application.status === 'rejected' && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center gap-2 text-sm text-red-700">
                            <span className="font-medium">❌</span>
                            <span className="font-medium">Application Rejected</span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Right Column - Notes */}
                    <div>
                      {application.notes && (
                        <div>
                          <h4 className="text-sm font-medium text-secondary-700 mb-2">Notes</h4>
                          <p 
                            className="text-sm text-secondary-600 leading-relaxed"
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 4,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {application.notes}
                          </p>
                        </div>
                      )}
                      
                      {application.coverLetter && (
                        <div className={application.notes ? 'mt-4' : ''}>
                          <h4 className="text-sm font-medium text-secondary-700 mb-2">Cover Letter</h4>
                          <p 
                            className="text-sm text-secondary-600 leading-relaxed"
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {application.coverLetter}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Application Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white mt-16 mb-20">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-secondary-900 mb-4">
                {editingApplication ? 'Edit Application' : 'Create Application'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Company Selection */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Company *
                    </label>
                    <select
                      value={formData.company}
                      onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value, referralContact: '' }))}
                      className="input w-full"
                      required
                    >
                      <option value="">Select a company</option>
                      {companies.map(company => (
                        <option key={company._id} value={company._id}>{company.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Role */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Job Role/Position *
                    </label>
                    <input
                      type="text"
                      value={formData.role}
                      onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                      className="input w-full"
                      placeholder="e.g., Software Engineer, Data Scientist"
                      required
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Status *
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                      className="input w-full"
                      required
                    >
                      {statusOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Resume Selection */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Resume
                    </label>
                    <select
                      value={formData.resume}
                      onChange={(e) => setFormData(prev => ({ ...prev, resume: e.target.value }))}
                      className="input w-full"
                    >
                      <option value="">No resume selected</option>
                      {resumes.map(resume => (
                        <option key={resume._id} value={resume._id}>
                          {resume.title || resume.originalName}
                          {resume.isDefault && ' (Default)'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Contact Selection */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Referral Contact
                    </label>
                    <select
                      value={formData.referralContact}
                      onChange={(e) => setFormData(prev => ({ ...prev, referralContact: e.target.value }))}
                      className="input w-full"
                      disabled={!formData.company || contactsLoading}
                    >
                      <option value="">No contact selected</option>
                      {contactsLoading ? (
                        <option disabled>Loading contacts...</option>
                      ) : (
                        filteredContacts.map(contact => (
                          <option key={contact._id} value={contact._id}>
                            {contact.name} {contact.title && `- ${contact.title}`}
                          </option>
                        ))
                      )}
                    </select>
                    {!formData.company && (
                      <p className="text-xs text-secondary-500 mt-1">Select a company first to see contacts</p>
                    )}
                  </div>

                  {/* Applied Date */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Applied Date *
                    </label>
                    <input
                      type="date"
                      value={formData.appliedDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, appliedDate: e.target.value }))}
                      className="input w-full"
                      required
                    />
                  </div>

                  {/* Source */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Source
                    </label>
                    <select
                      value={formData.source}
                      onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value as any }))}
                      className="input w-full"
                    >
                      {sourceOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Application URL */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Application URL
                    </label>
                    <input
                      type="url"
                      value={formData.applicationUrl}
                      onChange={(e) => setFormData(prev => ({ ...prev, applicationUrl: e.target.value }))}
                      className="input w-full"
                      placeholder="https://company.com/jobs/12345"
                    />
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Priority
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                      className="input w-full"
                    >
                      {priorityOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Follow Up Date */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Follow Up Date
                    </label>
                    <input
                      type="date"
                      value={formData.followUpDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, followUpDate: e.target.value }))}
                      className="input w-full"
                    />
                  </div>
                </div>

                {/* Salary Information */}
                <div className="border-t pt-6">
                  <h4 className="text-md font-medium text-secondary-900 mb-4">Salary Information (Optional)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Offered Salary
                      </label>
                      <input
                        type="number"
                        value={formData.salary?.offered || ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          salary: { 
                            ...prev.salary!, 
                            offered: e.target.value ? Number(e.target.value) : undefined 
                          }
                        }))}
                        className="input w-full"
                        placeholder="50000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Negotiated Salary
                      </label>
                      <input
                        type="number"
                        value={formData.salary?.negotiated || ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          salary: { 
                            ...prev.salary!, 
                            negotiated: e.target.value ? Number(e.target.value) : undefined 
                          }
                        }))}
                        className="input w-full"
                        placeholder="55000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Currency
                      </label>
                      <select
                        value={formData.salary?.currency}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          salary: { 
                            ...prev.salary!, 
                            currency: e.target.value 
                          }
                        }))}
                        className="input w-full"
                      >
                        <option value="INR">INR</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Period
                      </label>
                      <select
                        value={formData.salary?.period}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          salary: { 
                            ...prev.salary!, 
                            period: e.target.value as any 
                          }
                        }))}
                        className="input w-full"
                      >
                        <option value="hourly">Hourly</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Preperation Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="input w-full"
                    placeholder="Add any notes about this application..."
                  />
                </div>

                {/* Cover Letter */}
                {/* <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Cover Letter
                  </label>
                  <textarea
                    value={formData.coverLetter}
                    onChange={(e) => setFormData(prev => ({ ...prev, coverLetter: e.target.value }))}
                    rows={4}
                    className="input w-full"
                    placeholder="Paste your cover letter content here..."
                  />
                </div> */}

                {/* Follow Up Notes */}
                {formData.followUpDate && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Follow Up Notes
                    </label>
                    <textarea
                      value={formData.followUpNotes}
                      onChange={(e) => setFormData(prev => ({ ...prev, followUpNotes: e.target.value }))}
                      rows={2}
                      className="input w-full"
                      placeholder="What do you want to follow up about?"
                    />
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn btn-secondary px-5 py-2 rounded-md mr-1"
                    style={{ marginRight: '0.25rem' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary px-5 py-2 rounded-md ml-1"
                    style={{ marginLeft: '0.25rem' }}
                  >
                    {editingApplication ? 'Update Application' : 'Create Application'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Applications; 